import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const hasSubmittedRef = useRef(false);

  const token = React.useMemo(() => {
    const directToken = (params.get("token") || "").trim();
    if (directToken) {
      return decodeURIComponent(directToken);
    }

    for (const [key, value] of params.entries()) {
      if (!value && key.toLowerCase().startsWith("token=")) {
        return decodeURIComponent(key.slice(6).trim());
      }
    }

    const rawSearch = typeof window !== "undefined" ? window.location.search || "" : "";
    const decoded = decodeURIComponent(rawSearch.replace(/^\?/, ""));
    const match = decoded.match(/(?:^|[?&])token=([^&]+)/i) || decoded.match(/token=([^&]+)/i);
    return match?.[1]?.trim() || "";
  }, [params]);

  const submitVerification = useCallback(async () => {
    if (hasSubmittedRef.current) {
      return;
    }
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token.");
      return;
    }

    hasSubmittedRef.current = true;
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setStatus("success");
      setMessage("Email verified! You can now sign in.");
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || "Verification failed.");
    }
  }, [token]);

  useEffect(() => {
    submitVerification();
  }, [submitVerification]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg" data-testid="verify-email-title">Verify email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            data-testid="verify-email-status"
            className={`rounded-lg border px-4 py-3 text-sm ${
              status === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : status === "error"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            {status === "loading" ? "Verifying..." : message}
          </div>
          <Button
            asChild
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="verify-email-login-button"
          >
            <Link to="/login">Go to sign in</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
